import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import KNFormCheckbox from 'components/KNForm/KNFormCheckbox'
import { analyticsEvent } from 'global/helpers/analytics'
import { handleFormSubmission, processDefaultValues, processServerErrorMessages } from 'global/helpers/form'
import { type ReactElement, useEffect } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { booleanFormValuesToEnum, enumToBooleanFormValues } from './CarrierProfile.helpers'
import { CARRIER_PROFILE_QUERY_KEY, editCarrierProfile } from './CarrierProfile.service'
import { type CarrierProfile, TransportMode } from './CarrierProfile.types'

interface SelectTransportModesDialogPayload {
  readonly carrierProfile: CarrierProfile,
}

export interface SelectTransportModesDialogProps {
  readonly payload: SelectTransportModesDialogPayload
  readonly open: boolean
  readonly onAction: () => void
  readonly onClose: () => void
}

export type TransportModesFormValues = {
  [K in TransportMode]: boolean
}

const SelectTransportModesDialog = ({
  payload,
  open,
  onAction,
  onClose,
}: SelectTransportModesDialogProps): ReactElement => {
  const { t } = useTranslation()
  const { handleSubmit, reset, control, formState, setError } = useForm<TransportModesFormValues>()
  const allTransportModes = Object.values(TransportMode)
  const queryClient = useQueryClient()
  const updateCarrierProfileMutation = useMutation({
    mutationFn: async (data: TransportModesFormValues) => {
      await editCarrierProfile({
        ...payload.carrierProfile,
        transportModes: booleanFormValuesToEnum<TransportModesFormValues, TransportMode>(data),
      })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CARRIER_PROFILE_QUERY_KEY] }),
  })

  useEffect(() => {
    if (open) {
      reset(
        processDefaultValues(
          enumToBooleanFormValues<TransportMode, TransportModesFormValues>(allTransportModes, payload.carrierProfile.transportModes),
        ),
      )
    }
  }, [open])

  const onSubmit: SubmitHandler<TransportModesFormValues> = async (data: TransportModesFormValues): Promise<void> => {
    await handleFormSubmission(() => updateCarrierProfileMutation.mutateAsync(data), {
      onError: (error) => setError('root', processServerErrorMessages(error)),
      onSuccess: () => {
        onAction()
        analyticsEvent('polestar_carrier_profile_transport_modes_updated')
      },
    })
  }

  return (
    <KNDialog
      open={open}
      onClose={onClose}
      closeLabel={t('general.cancel')}
      preventClosing={formState.isSubmitting}
      title={t('screens.cs.carrier_profile.company.select_transport_modes')}
      actions={
        <Button
          type="submit"
          color="secondary"
          variant="contained"
          loading={formState.isSubmitting}
        >
          {t('general.save')}
        </Button>
      }
      onSubmit={handleSubmit(onSubmit)}
    >
      <KNDialogFormErrors errors={formState.errors?.root} />
      <Stack direction="column" spacing={1}>
        {allTransportModes.map((transportMode) => (
          <KNFormCheckbox
            key={transportMode}
            name={transportMode}
            control={control}
            label={t(`screens.cs.carrier_profile.transport_modes.${transportMode}`)}
          />
        ))}
      </Stack>
    </KNDialog>
  )
}

export default SelectTransportModesDialog
